import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { usePinFilters } from "@/lib/pins-context";
import type { SchoolPublic } from "@dugads/shared";
import * as api from "@/lib/api";

/**
 * Returns the school(s) the current user can access.
 *
 * - super_admin / org_admin: fetches the school list from the API.
 *   When org pins are active, filters to those orgs' schools.
 * - school_admin / coach: returns their scoped school directly (no API call)
 *
 * Returns { schools, selectedSchoolId, setSelectedSchoolId, loading, error, canPickSchool }
 */
export function useSchoolScope() {
  const { user } = useAuth();
  const { activeOrgIds } = usePinFilters();
  const [schools, setSchools] = useState<SchoolPublic[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const roles = user?.roles ?? [];
  const isSuperOrOrg = roles.some(
    (r) => r.role_type === "super_admin" || r.role_type === "org_admin",
  );
  const schoolRole = roles.find(
    (r) => (r.role_type === "school_admin" || r.role_type === "coach" || r.role_type === "super_parent") && r.scope_type === "school",
  );

  // Stable key for activeOrgIds to avoid re-renders
  const orgFilterKey = activeOrgIds.join(",");

  useEffect(() => {
    if (isSuperOrOrg) {
      // When org pins are active, fetch schools for each pinned org; otherwise fetch all
      const fetchPromise = activeOrgIds.length > 0
        ? Promise.all(activeOrgIds.map((orgId) => api.getSchools(orgId)))
            .then((results) => results.flatMap((r) => r.schools))
            .then((schools) => {
              // Deduplicate by id
              const seen = new Set<string>();
              return schools.filter((s) => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });
            })
        : api.getSchools().then((res) => res.schools);

      fetchPromise
        .then((filtered) => {
          setSchools(filtered);
          // Keep selection if still valid, otherwise select first
          if (filtered.length > 0) {
            setSelectedSchoolId((prev) => {
              const stillValid = filtered.some((s) => s.id === prev);
              return stillValid ? prev : filtered[0].id;
            });
          } else {
            setSelectedSchoolId("");
          }
        })
        .catch((e) =>
          setError(e instanceof api.ApiError ? e.message : "Failed to load schools"),
        )
        .finally(() => setLoading(false));
    } else if (schoolRole) {
      // Use the user's scoped school directly
      setSelectedSchoolId(schoolRole.scope_id);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [isSuperOrOrg, schoolRole?.scope_id, orgFilterKey]);

  return {
    schools,
    selectedSchoolId,
    setSelectedSchoolId,
    loading,
    error,
    /** true if the user can pick from multiple schools (super_admin/org_admin) */
    canPickSchool: isSuperOrOrg && schools.length > 1,
  };
}
